import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { api, errorText } from "../lib/api";
import { LinksBlock } from "../scene/ReadyPanel";

const Shell = ({ title, children, testId }) => (
  <main id="main" className="mx-auto max-w-2xl px-5 sm:px-8 pt-40 pb-28" data-testid={testId}>
    <h1 className="font-heading text-4xl sm:text-5xl">{title}</h1>
    <div className="mt-8">{children}</div>
  </main>
);

export function RecoverPage() {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/recover", { email });
      setSent(true);
    } catch (err) {
      toast.error(errorText(t, err));
    }
  };
  return (
    <Shell title={t("recover.title")} testId="recover-page">
      <p className="text-[hsl(var(--muted-foreground))] leading-relaxed">{t("recover.lead")}</p>
      {sent ? (
        <p className="mt-8 rounded-2xl bg-[hsl(var(--secondary))] p-5" data-testid="recover-sent">{t("recover.sent")}</p>
      ) : (
        <form onSubmit={submit} className="mt-8 flex flex-col sm:flex-row gap-3">
          <label className="flex-1">
            <span className="sr-only">{t("recover.email")}</span>
            <input type="email" required className="field-input" placeholder={t("recover.email")} value={email} onChange={(e) => setEmail(e.target.value)} data-testid="recover-email-input" />
          </label>
          <button className="btn-primary" data-testid="recover-submit-btn">{t("recover.send")}</button>
        </form>
      )}
    </Shell>
  );
}

export function RecoverConfirm() {
  const { token } = useParams();
  const { t } = useTranslation();
  const [state, setState] = useState({ status: "loading" });
  const done = useRef(false);
  useEffect(() => {
    if (done.current) return;
    done.current = true;
    api.post(`/recover/${token}`).then(({ data }) => setState({ status: "ok", ...data })).catch(() => setState({ status: "error" }));
  }, [token]);
  return (
    <Shell title={t("recover.confirm_title")} testId="recover-confirm-page">
      {state.status === "loading" && <p>{t("recover.confirm_loading")}</p>}
      {state.status === "error" && <p data-testid="recover-confirm-error">{t("recover.confirm_error")}</p>}
      {state.status === "ok" && (
        <div className="space-y-6">
          <p>{t("recover.confirm_done", { name: state.name })}</p>
          <LinksBlock name={state.name} slug={state.slug} token={state.manage_token} />
          <Link to={`/manage/${state.manage_token}`} className="btn-gold" data-testid="recover-open-manage-btn">{t("recover.open_manage")}</Link>
        </div>
      )}
    </Shell>
  );
}

export function VerifyEmail() {
  const { token } = useParams();
  const { t } = useTranslation();
  const [status, setStatus] = useState("loading");
  const done = useRef(false);
  useEffect(() => {
    if (done.current) return;
    done.current = true;
    api.post(`/verify-email/${token}`).then(() => setStatus("done")).catch(() => setStatus("error"));
  }, [token]);
  return (
    <Shell title={t("verify.title")} testId="verify-page">
      <p data-testid={`verify-${status}`}>{t(`verify.${status}`)}</p>
    </Shell>
  );
}

export function LegalPage({ kind }) {
  const { t } = useTranslation();
  const counts = { privacy: 9, cookies: 4, terms: 8 };
  return (
    <Shell title={t(`legal.${kind}.title`)} testId={`legal-${kind}-page`}>
      <p className="eyebrow mb-8">{t("legal.updated")}</p>
      <div className="space-y-5 leading-relaxed text-[hsl(var(--foreground))]/90">
        {Array.from({ length: counts[kind] }, (_, i) => <p key={i}>{t(`legal.${kind}.p${i + 1}`)}</p>)}
      </div>
    </Shell>
  );
}

export function NotFound() {
  const { t } = useTranslation();
  return (
    <Shell title="404" testId="not-found-page">
      <p className="text-[hsl(var(--muted-foreground))]">{t("errors.not_found")}</p>
      <Link to="/" className="btn-primary mt-8">{t("nav.home")}</Link>
    </Shell>
  );
}
